import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Cpu, MapPin, Radar } from 'lucide-react';
interface SlidePropositionProps {
  onContinue: () => void;
}
export function SlideProposition({
  onContinue
}: SlidePropositionProps) {
  const [stage, setStage] = useState(0);
  const pillars = [{
    title: 'The Snakkerfy Layer',
    desc: 'We take approved content and Snakkerfy it — short, relevant, interactive — then localize, publish and measure it from one place.',
    icon: <Cpu className="w-6 h-6 text-teal-400" data-id="element-1112" data-mp="24julonb7kzdj" />
  }, {
    title: 'Local relevance without local effort',
    desc: 'Markets get live, adapted campaigns for their own HCPs without spending affiliate hours they do not have.',
    icon: <MapPin className="w-6 h-6 text-sky-400" data-id="element-1113" data-mp="1vj663o662qyj" />
  }, {
    title: 'Full HQ visibility',
    desc: 'The Control Tower shows what is live in every market, how it performs and what should be reused.',
    icon: <Radar className="w-6 h-6 text-purple-500" data-id="element-1114" data-mp="3pukyyduh56ii" />
  }];
  useEffect(() => {
    const timers = [300, 800, 1300, 1800, 2500].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col items-center justify-center px-8 py-10 md:px-20 relative overflow-hidden" data-id="element-1115" data-mp="21x43o1l3onfs">
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none" data-id="element-1116" data-mp="1xywvhv4pcug7">
        <div className="w-[700px] h-[700px] bg-teal-400/5 rounded-full blur-[100px]" data-id="element-1117" data-mp="1a9bzobnga2pi" />
      </div>

      <div className="z-10 max-w-6xl w-full flex flex-col items-center" data-id="element-1118" data-mp="15a7sfqk36f1t">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center space-y-4 mb-10" data-id="element-1119" data-mp="1j8pdthmirfgk">
          <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1120" data-mp="2uow2vm949mx7">
            The Proposition
          </p>
          <h2 className="text-4xl md:text-6xl font-serif text-white leading-tight" data-id="element-1121" data-mp="1nb3i7gzke6mq">
            The Snakkerfy Layer,
            <br data-id="element-1122" data-mp="1s8lspbt8ctwu" />
            not another content factory
          </h2>
          <p className="text-lg text-white/60 max-w-3xl mx-auto" data-id="element-1123" data-mp="1wbi68o3wxztx">
            Snakkerfied content, live in every market — adjacent to LEO's
            production team, never in place of it.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full" data-id="element-1124" data-mp="14l3x156uhbol">
          {pillars.map((pillar, idx) => <motion.div key={pillar.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= idx + 1 ? 1 : 0,
          y: stage >= idx + 1 ? 0 : 20
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-7 h-full flex flex-col" data-id="element-1125" data-mp="18m7bbn98uznp" data-mpi={idx}>
              <div className="w-12 h-12 rounded-xl bg-teal-400/10 flex items-center justify-center mb-5" data-id="element-1126" data-mp="ptlkjhd71z9z">
                {pillar.icon}
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1127" data-mp="dc2f7bb52vy9">
                {pillar.title}
              </h3>
              <p className="text-white/60 leading-relaxed text-sm flex-1" data-id="element-1128" data-mp="8cg16znhlboe">
                {pillar.desc}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: stage >= 4 ? 180 : 0,
        opacity: stage >= 4 ? 1 : 0
      }} transition={{
        duration: 0.7
      }} className="h-[3px] bg-gradient-to-r from-blue-400 via-teal-400 to-teal-300 rounded-full my-10" data-id="element-1129" data-mp="3vhxa1q80ubh1" />

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        y: stage >= 5 ? 0 : 12
      }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl" data-id="element-1130" data-mp="3crfs8goq90ag">
          {['Global consistency', 'Local relevance', 'Zero affiliate effort'].map(item => <div key={item} className="bg-navy-900/50 border border-teal-400/30 rounded-2xl px-5 py-4 text-center" data-id="element-1131" data-mp="1206hpvynk6h4">
                <p className="text-white font-semibold" data-id="element-1132" data-mp="16wn8kxx8kyxw">{item}</p>
              </div>)}
        </motion.div>
      </div>
    </div>;
}